import { z } from 'zod';
/** UUID v4 — primary key shape for all our entities. */
export const UuidSchema = z.string().uuid();
/** ISO 3166-1 alpha-2 country code (FR, GB, MA, etc.). */
export const CountryCodeSchema = z
    .string()
    .length(2)
    .regex(/^[A-Z]{2}$/, 'Must be an ISO 3166-1 alpha-2 code');
/** ISO 4217 currency code (EUR, GBP, USD, etc.). */
export const CurrencyCodeSchema = z
    .string()
    .length(3)
    .regex(/^[A-Z]{3}$/, 'Must be an ISO 4217 currency code');
/**
 * ISO 639-1 language code for the customer-facing product (apps/web).
 *
 * Mirrors @clari/i18n SUPPORTED_LOCALES. Persisted in DB as
 * `users.preferred_language` and validated on every locale-aware endpoint.
 */
export const SupportedWebLocaleSchema = z.enum([
    'fr',
    'en',
    'ar',
    'es',
    'de',
    'it',
    'pt',
    'nl',
    'pl',
]);
/**
 * ISO 639-1 language code for the operator console (apps/admin).
 *
 * Intentionally restricted to FR/EN per COMPLIANCE-PLAYBOOK §7.bis —
 * sensitive operator decisions must stay readable by ACPR auditors.
 */
export const SupportedAdminLocaleSchema = z.enum(['fr', 'en']);
/**
 * @deprecated Use `SupportedWebLocaleSchema` for customer-facing surfaces
 * or `SupportedAdminLocaleSchema` for the operator console. Kept as an
 * alias for back-compat while callers migrate.
 */
export const LanguageCodeSchema = SupportedWebLocaleSchema;
/** E.164 phone number: leading +, 1–15 digits, first digit ≥ 1. */
export const PhoneE164Schema = z
    .string()
    .regex(/^\+[1-9]\d{1,14}$/, 'Must be in E.164 format (e.g. +33612345678)');
/** Email — lowercased, trimmed. */
export const EmailSchema = z.string().trim().toLowerCase().email();
/**
 * Person first/last name — deliberately PERMISSIVE.
 *
 * Swan rejects digits in names, so we block digits — but nothing else: we allow
 * apostrophes, hyphens, accents, spaces, etc. (O'Brien, Jean-Pierre, Müller,
 * N'Diaye). This is NOT a strict allowlist; Swan remains the final authority on
 * name validity. The error message is an i18n key the client resolves.
 */
export const PersonNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^\D*$/u, 'errors.name.contains_digits');
/** ISO 8601 timestamp string (used in JSON wire format). */
export const IsoTimestampSchema = z.string().datetime({ offset: true });
/**
 * Amount in minor units (cents).
 *
 * ⚠️ DB stores as BIGINT (CLAUDE.md §5 — "TOUJOURS en cents"). In TS we use
 * `number` for ergonomics; values up to 2^53 - 1 fit safely. If we ever need
 * to handle amounts beyond ~90 trillion cents, switch to `z.coerce.bigint()`.
 */
export const AmountCentsSchema = z.number().int().nonnegative();
/**
 * Same as AmountCentsSchema but allows negative values — for signed deltas
 * in transactions (debit = negative, credit = positive).
 */
export const SignedAmountCentsSchema = z.number().int();
/** IBAN — coarse validator (length + alphanumeric). Real check uses MOD 97. */
export const IbanSchema = z
    .string()
    .min(15)
    .max(34)
    .regex(/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/, 'Invalid IBAN format');
/** BIC/SWIFT code (8 or 11 chars). */
export const BicSchema = z
    .string()
    .regex(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, 'Invalid BIC/SWIFT format');
//# sourceMappingURL=primitives.js.map